import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Loader2, Save, Phone, Building2, ShieldAlert, Zap, Gift } from "lucide-react";
import api from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";

const ACTING_KEY = "novatus_acting_tenant_id";

export default function AdminTelesalesSettings() {
  const [user, setUser] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [actingTenant, setActingTenant] = useState(() => localStorage.getItem(ACTING_KEY) || "");
  const [ccEmail, setCcEmail] = useState("");
  const [tariff, setTariff] = useState("");
  // Empfehlungsbonus (Iter88za, 17.02.2026)
  const [bonusEnabled, setBonusEnabled] = useState(false);
  const [bonusAmount, setBonusAmount] = useState("500");
  const [bonusDescription, setBonusDescription] = useState("");
  const [bonusSaving, setBonusSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // 1. User laden
  useEffect(() => {
    try {
      const u = JSON.parse(localStorage.getItem("novatus_user") || "null");
      setUser(u);
    } catch { /* ignore */ }
  }, []);

  // 2. Super-Admin: Mandanten-Liste laden (für inline Switcher)
  useEffect(() => {
    if (!user?.is_super_admin) return;
    api.get("/superadmin/tenants")
      .then((r) => setTenants((r.data || []).filter((t) => t.active !== false)))
      .catch(() => { /* nicht-kritisch */ });
  }, [user]);

  // 3. Settings laden — aber nur wenn Tenant-Kontext vorhanden
  useEffect(() => {
    if (!user) return;
    if (user.is_super_admin && !actingTenant) {
      setLoading(false);
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/admin/telesales/settings");
        setCcEmail(data.lead_cc_email || "");
        setTariff(data.electricity_tariff_eur_per_kwh != null ? String(data.electricity_tariff_eur_per_kwh) : "");
      } catch (e) {
        toast.error(e.response?.data?.detail || "Laden fehlgeschlagen");
      }
      // Empfehlungsbonus separat laden
      try {
        const { data: b } = await api.get("/admin/empfehlungsbonus/settings");
        setBonusEnabled(!!b.enabled);
        setBonusAmount(b.amount_eur != null ? String(b.amount_eur) : "500");
        setBonusDescription(b.description || "");
      } catch (e) {
        // still not-fatal
      } finally { setLoading(false); }
    })();
  }, [user, actingTenant]);

  const pickTenant = (tid) => {
    if (tid) {
      localStorage.setItem(ACTING_KEY, tid);
    } else {
      localStorage.removeItem(ACTING_KEY);
    }
    setActingTenant(tid);
    // Reload data with new scope (without full page reload to keep UX snappy)
    setCcEmail("");
  };

  const save = async () => {
    if (ccEmail && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(ccEmail)) {
      toast.error("Ungültige E-Mail-Adresse");
      return;
    }
    if (user?.is_super_admin && !actingTenant) {
      toast.error("Bitte zuerst oben einen Mandanten wählen");
      return;
    }
    setSaving(true);
    try {
      const payload = { lead_cc_email: ccEmail || null };
      if (tariff) {
        const t = parseFloat(String(tariff).replace(",", "."));
        if (!Number.isFinite(t) || t < 0.10 || t > 1.00) {
          toast.error("Stromtarif muss zwischen 0,10 € und 1,00 € pro kWh liegen.");
          setSaving(false);
          return;
        }
        payload.electricity_tariff_eur_per_kwh = t;
      }
      await api.put("/admin/telesales/settings", payload);
      toast.success("Gespeichert");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  const saveBonus = async () => {
    if (user?.is_super_admin && !actingTenant) {
      toast.error("Bitte zuerst oben einen Mandanten wählen");
      return;
    }
    const amt = parseFloat(String(bonusAmount).replace(",", "."));
    if (!Number.isFinite(amt) || amt < 0 || amt > 5000) {
      toast.error("Betrag muss zwischen 0 € und 5.000 € liegen.");
      return;
    }
    setBonusSaving(true);
    try {
      await api.put("/admin/empfehlungsbonus/settings", {
        enabled: bonusEnabled,
        amount_eur: amt,
        description: bonusDescription || null,
      });
      toast.success("Empfehlungsbonus gespeichert");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setBonusSaving(false); }
  };

  const isSuper = !!user?.is_super_admin;
  const selectedTenantName = tenants.find((t) => t.id === actingTenant)?.name;
  const needsTenantPick = isSuper && !actingTenant;

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="admin-telesales-settings">
      <AppHeader title="Telesales — Einstellungen" back="/dashboard" />
      <main className="max-w-2xl mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-4">
        <div className="flex items-center gap-3 mb-2">
          <Phone className="text-[#00E575]" size={22} />
          <div>
            <h1 className="font-display text-xl sm:text-2xl font-bold">Telesales-Einstellungen</h1>
            <p className="text-[12px] text-zinc-300 font-medium">Mandanten-spezifische Konfiguration für die Schnellerfassung</p>
          </div>
        </div>

        {/* Super-Admin Mandanten-Auswahl */}
        {isSuper && (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-3.5">
            <label className="text-[12px] uppercase tracking-widest text-zinc-300 font-semibold font-bold mb-1.5 flex items-center gap-1.5">
              <Building2 size={11} /> Mandant wählen
            </label>
            <select
              value={actingTenant}
              onChange={(e) => pickTenant(e.target.value)}
              data-testid="ts-settings-tenant-select"
              className="h-10 w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 text-zinc-50 focus:border-[#00E575] outline-none text-[13px] font-medium"
            >
              <option value="">— Mandant auswählen —</option>
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>{t.name} ({t.slug})</option>
              ))}
            </select>
            {selectedTenantName && (
              <div className="text-[12px] text-zinc-300 mt-2">
                Bearbeite Settings für: <span className="text-[#00E575] font-semibold">{selectedTenantName}</span>
              </div>
            )}
          </div>
        )}

        {needsTenantPick ? (
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-[12.5px] text-amber-200 flex items-start gap-2" data-testid="ts-settings-no-tenant">
            <ShieldAlert size={16} className="mt-0.5 shrink-0" />
            <div>
              <div className="font-bold mb-0.5">Mandant noch nicht gewählt</div>
              Wählen Sie oben den Mandanten, dessen Telesales-Settings Sie bearbeiten möchten.
            </div>
          </div>
        ) : loading ? (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-8 flex justify-center">
            <Loader2 className="animate-spin text-zinc-400" size={22} />
          </div>
        ) : (
          <section className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 space-y-4">
            <div>
              <label className="block text-[12px] uppercase tracking-widest text-zinc-200 mb-1 font-bold">
                <Mail size={11} className="inline mr-1" /> Lead-CC-E-Mail
              </label>
              <input
                type="email"
                value={ccEmail}
                onChange={(e) => setCcEmail(e.target.value)}
                data-testid="cc-email-input"
                placeholder="z.B. vertriebsleitung@deinefirma.de"
                className="h-11 w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 text-zinc-50 focus:border-[#00E575] outline-none text-[14px] font-medium"
              />
              <p className="text-[12px] text-zinc-300 mt-2 leading-relaxed">
                Diese Adresse erhält bei jedem über Telesales erfassten Lead eine Kopie des Orientierungsangebots
                (z.B. zur Qualitätskontrolle oder zur internen Verteilung).
                Leer lassen, wenn keine CC gewünscht.
              </p>
            </div>

            <div className="pt-4 border-t border-zinc-800">
              <label className="block text-[12px] uppercase tracking-widest text-zinc-200 mb-1 font-bold">
                <Zap size={11} className="inline mr-1" /> Stromtarif (für Wirtschaftlichkeit & PV-Berechnung)
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  inputMode="decimal"
                  value={tariff}
                  onChange={(e) => setTariff(e.target.value)}
                  data-testid="electricity-tariff-input"
                  placeholder="0.32"
                  className="h-11 w-32 bg-[#09090B] border border-zinc-700 rounded-lg px-3 text-zinc-50 focus:border-[#00E575] outline-none text-[14px] font-medium"
                />
                <span className="text-[13px] text-zinc-400">€ / kWh</span>
              </div>
              <p className="text-[12px] text-zinc-300 mt-2 leading-relaxed">
                Verwendet für die Stromkosten-Annahme im WP-Wirtschaftlichkeitsmodell
                und für die PV-Eigenverbrauchs-Einsparung im Orientierungsangebot.
                Leer lassen für Default-Wert (0,32 €/kWh).
              </p>
            </div>
            <div className="flex justify-end">
              <button
                onClick={save}
                disabled={saving}
                data-testid="cc-email-save"
                className="h-10 px-5 rounded-xl bg-[#00E575] text-[#09090B] font-bold inline-flex items-center gap-2 hover:bg-[#00C964] disabled:opacity-50"
              >
                {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                Speichern
              </button>
            </div>
          </section>
        )}

        {/* Empfehlungsbonus (Iter88za, 17.02.2026) — separate Sektion mit
            eigenem Save-Button, damit Admin gezielt speichern kann. */}
        {!needsTenantPick && !loading && (
          <section className="bg-[#18181B] border border-amber-500/40 rounded-2xl p-5 space-y-4" data-testid="empfehlungsbonus-panel">
            <div className="flex items-center gap-2 mb-1">
              <Gift className="text-amber-400" size={18} />
              <h2 className="font-bold text-[15px] text-amber-200">Empfehlungsbonus</h2>
            </div>
            <p className="text-[12.5px] text-zinc-300 leading-relaxed">
              Wenn aktiv, sieht der Kunde beim digitalen Signieren (QR-Code) eine Checkbox
              „Empfehlungsbonus in Anspruch nehmen&quot;. Der Bonus ersetzt einen ggf. bereits
              im Angebot enthaltenen Rabatt. Nach dem Anklicken durch den Kunden gibt der
              Sales-Rep den Bonus intern manuell frei (Push- + E-Mail-Benachrichtigung).
            </p>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={bonusEnabled}
                onChange={(e) => setBonusEnabled(e.target.checked)}
                data-testid="bonus-enabled-toggle"
                className="h-5 w-5 rounded border-zinc-600 bg-[#09090B] text-amber-500 focus:ring-amber-500"
              />
              <span className="text-[13px] font-semibold text-zinc-100">Empfehlungsbonus für Kunden anzeigen</span>
            </label>

            <div>
              <label className="block text-[12px] uppercase tracking-widest text-zinc-200 mb-1 font-bold">Bonus-Betrag (€)</label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  inputMode="decimal"
                  value={bonusAmount}
                  onChange={(e) => setBonusAmount(e.target.value)}
                  data-testid="bonus-amount-input"
                  placeholder="500"
                  className="h-11 w-32 bg-[#09090B] border border-zinc-700 rounded-lg px-3 text-zinc-50 focus:border-amber-500 outline-none text-[14px] font-medium"
                />
                <span className="text-[13px] text-zinc-400">€</span>
              </div>
            </div>

            <div>
              <label className="block text-[12px] uppercase tracking-widest text-zinc-200 mb-1 font-bold">Beschreibung (Text auf Signatur-Seite)</label>
              <textarea
                rows={3}
                value={bonusDescription}
                onChange={(e) => setBonusDescription(e.target.value)}
                data-testid="bonus-description-input"
                placeholder="Wurden Sie von einem zufriedenen Kunden empfohlen? …"
                className="w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 focus:border-amber-500 outline-none text-[13px] leading-snug"
              />
              <p className="text-[11px] text-zinc-400 mt-1">
                Wird dem Kunden im Public-QR-Sign-UI direkt unter der Checkbox angezeigt.
                Leer lassen für Standardtext.
              </p>
            </div>

            <div className="flex justify-end">
              <button
                onClick={saveBonus}
                disabled={bonusSaving}
                data-testid="bonus-save"
                className="h-10 px-5 rounded-xl bg-amber-500 text-[#09090B] font-bold inline-flex items-center gap-2 hover:bg-amber-400 disabled:opacity-50"
              >
                {bonusSaving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                Bonus-Einstellungen speichern
              </button>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
